'use client';

/**
 * CategoryCatalogCard — mode11's no-selection landing canvas
 * (content_key="category_catalog").
 *
 * The category-first counterpart to mode4's vendor_catalog: one tile
 * per top-level category (seating, table, decor …) with its live
 * count, from the SAME query-less product_search facet payload
 * category_tree already fetches (`{query: '', filters: {}, limit: 1}`,
 * read off the `category` facet) — not a flat 16k-product grid, which
 * is what the default canvas showed before this card existed
 * (2026-10-04: "the original goal was to show the product categories
 * … not show 13K products").
 *
 * Click semantics mirror CategoryTreeCard's bucket click exactly (the
 * same store writes): scope to the category, flip the canvas to the
 * results grid. This card and category_tree are two independent
 * entry points to the identical filter state, not two sources of
 * truth — clicking either one lands on the same scoped results.
 *
 * @see klyde_edge/ui_config/composition/cards/search.py — content_key="category_catalog"
 * @see ../../../explorer/cards/search/category-tree-card.tsx — the rail twin, same fetch + click shape
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo } from 'react';
import { Loader2, Shapes, XCircle } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useProductSearchStore } from '@/lib/stores/product-search-store';
import { useProductLabel, useCardProps } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { Facet } from './facet-card';
import { CardGrid } from '../card-grid';

interface ProductSearchResponse {
  total: number;
  facets: Facet[];
}

export function CategoryCatalogCard() {
  const setFilter = useProductSearchStore((s) => s.setFilter);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const strategyName = useCapabilityStore((s) => s.strategyName('product_intelligence', 'search'));

  const labelTitle = useProductLabel('cat_catalog_title');
  const labelLoading = useProductLabel('cat_tree_loading');
  const labelError = useProductLabel('cat_tree_error');

  // Same BFF-declared facet key + query-less browse params category_tree
  // reads — one fetch, two independent paints (rail tree, canvas tiles).
  const props = useCardProps('category_catalog');
  const facetKey = (props.facet_key as string | undefined) ?? 'category';
  const browseParams = useMemo(
    () => (props.browse_params as object | undefined) ?? { query: '', filters: {}, limit: 1 },
    [props],
  );

  const { data, isLoading, error } = useStrategyData<ProductSearchResponse>(
    strategyName,
    browseParams,
    !!strategyName,
  );
  const res = data?.data;
  const facet = res?.facets?.find((f) => f.key === facetKey);
  const values = facet?.values ?? [];

  const queryKey = useMemo(
    () => ['strategy-data', strategyName ?? '', browseParams] as const,
    [strategyName, browseParams],
  );
  useCardExport(useMemo(() => ({
    label: labelTitle,
    content_key: 'category_catalog',
    surface: 'canvas' as const,
    status: (isLoading ? 'loading' : error ? 'error' : 'loaded') as 'loading' | 'loaded' | 'error',
    query_key: queryKey,
    text: values.map((v) => `${v.label ?? v.value} (${v.count})`).join('\n'),
    json: { categories: values.map((v) => ({ value: v.value, count: v.count })) },
  }), [labelTitle, isLoading, error, queryKey, values]));

  const pickCategory = (value: string) => {
    setFilter(facetKey, [value]);
    setExplorerSelection('search');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(text.body, 'flex items-center justify-center h-full gap-2 text-destructive')}>
        <XCircle className="h-4 w-4" />
        {labelError}
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-3">
      <h2 className="text-lg font-medium flex items-center gap-2">
        <Shapes className="h-4 w-4 text-muted-foreground" />
        {labelTitle}
      </h2>
      <CardGrid>
        {values.map((v) => (
          <button
            key={v.value}
            type="button"
            onClick={() => pickCategory(v.value)}
            className="flex flex-col gap-1 rounded-lg border border-border bg-card p-4 text-left hover:border-primary/40 hover:bg-accent/30 transition-colors"
          >
            <span className="text-sm font-medium capitalize truncate">{v.label ?? v.value}</span>
            <span className={cn(text.muted)}>{v.count.toLocaleString()}</span>
          </button>
        ))}
      </CardGrid>
    </div>
  );
}
